/**
 * AdminRoute Component
 *
 * Wraps routes that require admin privileges. Redirects to projects page if user is not an admin.
 *
 * Features:
 * - Shows loading state while checking authentication
 * - Redirects to /login if not authenticated
 * - Redirects to /projects if authenticated but not an admin
 * - Only renders children if user is authenticated and is an admin
 */

import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/auth'
import toast from 'react-hot-toast'
import { useEffect, useRef } from 'react'

interface AdminRouteProps {
  children: React.ReactNode
}

/**
 * AdminRoute - Route wrapper that requires admin authentication
 *
 * @param children - The component to render if authenticated as admin
 * @returns Either the protected component, redirect to login, or redirect to projects
 */
export default function AdminRoute({ children }: AdminRouteProps) {
  const { user, isLoading } = useAuth()
  const location = useLocation()
  const hasShownToast = useRef(false)

  // Show toast when non-admin user tries to access admin route
  useEffect(() => {
    if (!isLoading && user && !user.is_admin && !hasShownToast.current) {
      toast.error('This page is only accessible to administrators')
      hasShownToast.current = true
    }
  }, [isLoading, user])

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen app-shell">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--rq-text)] mx-auto mb-4"></div>
          <p className="text-[var(--rq-text-soft)]">Loading...</p>
        </div>
      </div>
    )
  }

  // If not authenticated, redirect to login with return URL
  if (!user) {
    const returnUrl = encodeURIComponent(location.pathname + location.search)
    return <Navigate to={`/login?returnUrl=${returnUrl}`} replace />
  }

  // If authenticated but not admin, redirect to projects page
  if (!user.is_admin) {
    return <Navigate to="/projects" replace />
  }

  // User is authenticated and is admin, render the protected component
  return <>{children}</>
}
